import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Stethoscope } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { familyAPI } from '@/lib/api';

/* Karte „Check-Up“ (11.10.2026, Betreiber: die Körperanalyse wird Teil des Check-Ups — Kontrollmessungen).
   Steht an zwei Orten: Ernährung › Mitglied (immer, direkt über der Karte „Körperanalyse“) und in der Lead-Ansicht
   (nur mit Mitgliedschaft oder mindestens einem Check-Up). Sie zeigt Tage, Kontingent und — nur wenn der Server es
   liefert (Recht ernaehrung.view) — OB an einem Tag gemessen wurde. Nie einen Messwert: Die Werte stehen in der
   Karte „Körperanalyse“. Alles kommt vom Server (backend/checkup.py, routes/family.py); gerechnet wird hier nichts.
   Eintragen und „Tag ändern“ brauchen das Recht leads.edit (`darf_eintragen`), der Server prüft es ohnehin.
   Je Person und Tag gibt es einen Check-Up: Derselbe Tag noch einmal zählt nicht gegen das Kontingent.
   Die Messung beim Check-Up ist ein ANGEBOT (Entscheidung des Betreibers 11.10.2026): Die Zeile zur Messung sagt
   das, drängt nicht, und fordert nach einem Widerruf der Einwilligung nicht zum Messen auf. */

const tagText = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '');
const euro = (v) => `${Number.isInteger(Number(v)) ? Number(v) : Number(v).toFixed(2).replace('.', ',')} €`;
const ZEITFENSTER = { vormittag: 'vormittags', nachmittag: 'nachmittags', abend: 'abends' };
// Warum für dieses Mitglied nicht gemessen werden kann — dieselben Sätze wie in der Karte „Körperanalyse“
const GRUND = {
  kein_coaching: 'Körperanalyse nur für Mitglieder mit laufendem Ernährungscoaching (Einstellung „für“).',
  kein_mitglied: 'Keine laufende Mitgliedschaft und kein laufendes Ernährungscoaching.',
  unter_18: 'Unter 18 Jahren werden keine Messungen angenommen.',
  alter_unbekannt: 'Alter unbekannt: bitte das Geburtsdatum in der Akte oder das Alter im Kundenprofil eintragen (Messung erst ab 18).',
};

function kontingentText(k) {
  if (k.mitgliedschaft && k.inklusive > 0) return k.offen > 0 ? `noch ${k.offen} inklusive` : `Kontingent aufgebraucht – ${euro(k.preis)} je Termin`;
  return `kein Inklusiv-Kontingent – ${euro(k.preis)} je Termin`;
}

// Was vor dem Eintragen feststeht: woraus der Check-Up bezahlt wird — aus dem Stand, den der Kopf der Karte zeigt
function zaehlText(k) {
  if (k.mitgliedschaft && k.inklusive > 0) {
    return k.offen > 0 ? `Zählt gegen das Inklusiv-Kontingent (noch ${k.offen}).` : `Das Inklusiv-Kontingent ist aufgebraucht – ${euro(k.preis)} fällig.`;
  }
  return `Kein Inklusiv-Kontingent – ${euro(k.preis)} fällig.`;
}

const ANGEBOT = 'Die Messung ist ein Angebot – der Check-Up gilt auch ohne.';

/* Die Zeile zur Körperanalyse. `inDerAkte`: Dort gibt es die Karte „Körperanalyse“ nicht, nur den Link dorthin.
   `einwilligung` (liegt_vor | fehlt | frueher | widerrufen) kommt vom Server; fehlt das Feld (älterer Server), gilt
   der Satz für „liegt vor“. */
function messzeile(stand, inDerAkte) {
  const ka = stand.koerperanalyse;
  if (!ka) return null;                                   // ohne ernaehrung.view: nichts zur Messung
  if (!ka.aktiv) return 'Körperanalyse ist ausgeschaltet – Check-Up ohne Messung.';
  if (!ka.darf) return `${GRUND[ka.grund] || 'Für dieses Mitglied ist keine Messung möglich.'} Check-Up ohne Messung.`;
  if (stand.messung_heute) return 'Messung von heute liegt vor.';
  if (ka.einwilligung === 'widerrufen') {
    return 'Das Mitglied hat die Einwilligung zur Körperanalyse widerrufen – der Check-Up gilt ohne Messung. Gemessen wird nur, wenn es von sich aus neu einwilligt.';
  }
  const wo = inDerAkte ? 'unter „Körperanalyse öffnen“' : 'in der Karte „Körperanalyse“ darunter';
  if (ka.einwilligung === 'fehlt' || ka.einwilligung === 'frueher') {
    const lage = ka.einwilligung === 'frueher'
      ? 'Das Mitglied hat bisher nur einer früheren Fassung der Einwilligung zugestimmt'
      : 'Eine Einwilligung des Mitglieds liegt noch nicht vor';
    return `${ANGEBOT} ${lage}: Möchte es messen, stimmt es in der App zu – oder du bestätigst beim Eintragen der Werte ${wo}, dass die Einwilligung auf Papier vorliegt.`;
  }
  return `${ANGEBOT} Heute liegt noch keine vor: Möchte das Mitglied messen, scannt es in der App – oder du trägst die Werte ${wo} ein.`;
}

export default function CheckupKarte({ leadId, onGeaendert, immer = false, mitLink = false, neuLadenBei }) {
  const [stand, setStand] = useState(null);
  const [offen, setOffen] = useState(false);     // die Zeile „Check-Up eintragen“ ist aufgeklappt
  const [bereit, setBereit] = useState(false);   // „Ja“ erst kurz nach dem Aufklappen: ein Doppelklick überspringt die Rückfrage sonst
  const [datum, setDatum] = useState('');
  const [aendern, setAendern] = useState(null);  // { von, nach } — „Tag ändern“ an einer Zeile
  const [alle, setAlle] = useState(false);
  const [busy, setBusy] = useState(false);
  // Antworten gelten nur für den Lead und den Lauf, zu dem sie gehören (Blättern zwischen Leads)
  const lauf = useRef(0);

  const lade = useCallback(async () => {
    const nr = ++lauf.current;
    // Ohne Zugriff (weder Lead noch Ernährung) oder bei einem Fehler bleibt die Karte weg
    try { const r = await familyAPI.checkupStand(leadId); if (nr === lauf.current) setStand(r.data); }
    catch { if (nr === lauf.current) setStand(null); }
  }, [leadId]);
  useEffect(() => { setStand(null); setOffen(false); setAendern(null); setBusy(false); setAlle(false); }, [leadId]);
  useEffect(() => { lade(); return () => { lauf.current += 1; }; }, [lade, neuLadenBei]);
  useEffect(() => {
    setBereit(false);
    if (!offen) return undefined;
    const t = setTimeout(() => setBereit(true), 500);
    return () => clearTimeout(t);
  }, [offen]);

  const ausfuehren = async (aufruf, meldung, fehlerText) => {
    const nr = lauf.current;
    setBusy(true);
    try {
      const r = await aufruf();
      if (nr !== lauf.current) return;
      meldung(r.data || {});
    } catch (e) {
      if (nr !== lauf.current) return;
      const d = e?.response?.data?.detail;
      toast.error(e?.response?.status === 403 ? 'Dafür fehlt dir das Recht „Leads bearbeiten“.' : typeof d === 'string' ? d : fehlerText);
    }
    await lade();
    if (nr !== lauf.current - 1) return;         // inzwischen ein anderer Lead: dessen Karte nicht anfassen
    setOffen(false);
    setAendern(null);
    setBusy(false);
    onGeaendert?.();                              // die Karte „Körperanalyse“ darunter lädt neu (Marke „Kontrollmessung“)
  };
  const eintragen = () => ausfuehren(
    () => familyAPI.recordCheckup(leadId, datum),
    (d) => {
      // der Satz kommt vom Server (Tag als TT.MM.JJJJ); ohne ihn (älterer Server) aus dem gelieferten Tag
      const am = d.tag ? ` am ${tagText(d.tag)}` : '';
      if (d.neu === false) { toast.info(d.message || `Check-Up${am} war schon eingetragen – nichts gezählt`); return; }
      toast.success(`${d.message || `Check-Up${am} eingetragen`}${d.preis_faellig > 0 ? ` · ${euro(d.preis_faellig)} fällig` : d.aus_kontingent ? ` · noch ${d.inklusive_offen} inklusive` : ''}`);
    },
    'Check-Up nicht eingetragen');
  const verlegen = () => ausfuehren(
    () => familyAPI.checkupVerlegen(leadId, aendern.von, aendern.nach),
    () => toast.success(`Check-Up auf den ${tagText(aendern.nach)} geändert`),
    'Tag nicht geändert');
  const neuLaden = async () => { setBusy(true); await lade(); setBusy(false); onGeaendert?.(); };

  if (!stand) return null;
  const k = stand.kontingent;
  // In der Akte nur bei Mitgliedern und wo es schon einen Check-Up gibt — sonst stünde die Karte bei jedem Interessenten
  if (!immer && !k.mitgliedschaft && stand.checkups.length === 0) return null;
  const darf = !!stand.darf_eintragen;
  const liste = alle ? stand.checkups : stand.checkups.slice(0, 6);
  // nicht nach heute und nicht vor dem frühesten Tag des Servers (ein vertipptes Jahr zählte sonst das Kontingent).
  // Ein Tag, der schon in der Liste steht, bleibt wählbar, wie alt er auch ist — so hält es auch der Server.
  const gueltig = (t) => /^\d{4}-\d{2}-\d{2}$/.test(t || '') && t <= stand.heute
    && (!stand.fruehester || t >= stand.fruehester || stand.checkups.some((c) => c.tag === t));
  const messung = messzeile(stand, mitLink);

  return (
    <Card className="border-border/60" data-testid="checkup-karte">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 flex-wrap"><Stethoscope className="w-4 h-4" aria-hidden="true" /> Check-Up
          <span className="ml-auto text-xs font-normal text-muted-foreground" data-testid="checkup-kontingent">{kontingentText(k)}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-2 text-sm">
        {stand.anfrage && (
          <p className="text-xs text-muted-foreground" data-testid="checkup-anfrage">
            {stand.anfrage.status === 'bestaetigt'
              ? `Termin: ${stand.anfrage.termin || '–'}`
              : `Anfrage offen, Wunsch: ${tagText(stand.anfrage.wunschdatum)}${stand.anfrage.zeitfenster ? ` ${ZEITFENSTER[stand.anfrage.zeitfenster] || stand.anfrage.zeitfenster}` : ''}`}
          </p>
        )}

        {darf && (offen ? (
          <div className="space-y-1.5 rounded-md border p-2" data-testid="checkup-frage">
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              Tag des Check-Ups
              <Input type="date" value={datum} min={stand.fruehester || undefined} max={stand.heute} onChange={(e) => setDatum(e.target.value)} className="h-8 w-40" data-testid="checkup-datum" />
            </label>
            <p className="text-xs text-amber-700 dark:text-amber-400" data-testid="checkup-rueckfrage">{zaehlText(k)} Je Person und Tag gibt es einen Check-Up – derselbe Tag noch einmal zählt nicht. Ein eingetragener Check-Up lässt sich nicht löschen, nur sein Tag lässt sich ändern.</p>
            <span className="flex items-center gap-2">
              <Button size="sm" disabled={busy || !bereit || !gueltig(datum)} onClick={eintragen} data-testid="checkup-ja">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ja, eintragen'}
              </Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setOffen(false)}>Abbrechen</Button>
            </span>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={() => { setDatum(stand.heute); setAendern(null); setOffen(true); }} data-testid="checkup-eintragen">Check-Up eintragen</Button>
        ))}

        {!darf && <p className="text-xs text-muted-foreground" data-testid="checkup-nur-lesen">Nur zum Lesen: Check-Ups trägt ein, wer Leads bearbeiten darf (Recht „Leads bearbeiten“).</p>}

        {stand.checkups.length === 0 ? (
          <p className="text-xs text-muted-foreground">Noch kein Check-Up eingetragen.</p>
        ) : (
          <div>
            <h3 className="text-xs font-semibold mb-1">Bisherige Check-Ups</h3>
            <ul className="divide-y text-xs" data-testid="checkup-liste">
              {liste.map((c) => (
                <li key={c.tag} className="py-1.5">
                  {aendern && aendern.von === c.tag ? (
                    <span className="flex items-center gap-2 flex-wrap">
                      <Input type="date" value={aendern.nach} min={stand.fruehester || undefined} max={stand.heute} onChange={(e) => setAendern({ ...aendern, nach: e.target.value })} className="h-8 w-40" aria-label={`Neuer Tag für den Check-Up vom ${tagText(c.tag)}`} />
                      <Button size="sm" disabled={busy || !gueltig(aendern.nach) || aendern.nach === c.tag} onClick={verlegen}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Speichern'}</Button>
                      <Button size="sm" variant="ghost" disabled={busy} onClick={() => setAendern(null)}>Abbrechen</Button>
                      <span className="basis-full text-[11px] text-muted-foreground">Nur für einen falsch eingetragenen Tag: zählt nicht noch einmal gegen das Kontingent.</span>
                      {c.messung === true && <span className="basis-full text-[11px] text-amber-700 dark:text-amber-400" data-testid="checkup-aendern-messung">Die Messung vom {tagText(c.tag)} zählt danach nicht mehr als Kontrollmessung – außer am neuen Tag liegt auch eine vor.</span>}
                    </span>
                  ) : (
                    <span className="flex items-start justify-between gap-2">
                      <span className="min-w-0">
                        <span className="font-medium tabular-nums">{tagText(c.tag)}</span>
                        <span className="text-muted-foreground">
                          {/* dreiwertig vom Server: true = aus dem Kontingent, false = nicht (der Preis von damals steht
                              nirgends, darum keine Zahl), null = eingetragen, aber nicht gezählt */}
                          {c.altbestand ? ' · aus dem Altbestand' : c.aus_kontingent === true ? ' · aus Kontingent' : c.aus_kontingent === false ? ' · nicht aus dem Kontingent' : ' · nicht gezählt'}
                          {c.erfasst_von ? ` · von ${c.erfasst_von}` : ''}
                          {c.verlegt_von ? ` · Tag geändert (vorher ${tagText(c.verlegt_von)})` : ''}
                          {c.messung === true ? ' · mit Messung' : c.messung === false ? ' · ohne Messung' : ''}
                        </span>
                      </span>
                      {darf && <button type="button" className="shrink-0 underline underline-offset-2 text-muted-foreground hover:text-foreground" onClick={() => { setOffen(false); setAendern({ von: c.tag, nach: c.tag }); }}>Tag ändern</button>}
                    </span>
                  )}
                </li>
              ))}
            </ul>
            {stand.checkups.length > 6 && <Button type="button" size="sm" variant="ghost" onClick={() => setAlle(!alle)}>{alle ? 'Weniger zeigen' : `Alle ${stand.checkups.length} zeigen`}</Button>}
          </div>
        )}

        {messung && <p className="text-xs text-muted-foreground" role="status" data-testid="checkup-messung">{messung}</p>}
        <div className="flex items-center gap-3 flex-wrap">
          <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={neuLaden} data-testid="checkup-neu-laden"><RefreshCw className="w-3.5 h-3.5 mr-1" aria-hidden="true" />Neu laden</Button>
          {mitLink && stand.koerperanalyse && <Link to={`/ernaehrung/${leadId}`} className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground">Körperanalyse öffnen</Link>}
        </div>
      </CardContent>
    </Card>
  );
}
